/* eslint-disable better-tailwindcss/no-unknown-classes */
import React, { useState } from 'react';

import { AppsFillIcon, TokenFilledIcon } from '@infinex/ui-icons';
import { cn } from '@infinex/ui-styles';
import { ChainEmblem, Skeleton, SkeletonMedia } from '@infinex/ui/components';

import { isPlaceholderDappIcon } from '../utils';

export type EmblemPosition =
  | 'top-right'
  | 'top-left'
  | 'bottom-right'
  | 'bottom-left';

export type InfoImageProps = {
  src: string | null;
  chain?: string | null;
  customEmblemSrc?: string | null;
  customEmblemIcon?: React.ReactNode; // Custom icon component
  alt?: string;
  size?: number;
  badgeSize?: number;
  className?: string;
  haloBg?: string; // CSS color for halo
  haloSize?: number; // outward size of halo in px
  emblemPosition?: EmblemPosition;
};

export function InfoImage({
  src,
  alt = '',
  size = 34,
  chain,
  customEmblemSrc,
  customEmblemIcon,
  badgeSize = 20,
  className,
  haloBg = 'var(--surfaceTwo, #111)',
  haloSize = 3,
  emblemPosition = 'bottom-right',
}: InfoImageProps) {
  const isPlaceholderIcon = isPlaceholderDappIcon(src);
  const [isLoading, setIsLoading] = useState(!isPlaceholderIcon); // only load if we have a non placeholder icon
  const [hasError, setHasError] = useState(false);

  const badgePad = 4;
  const glyphSize = Math.max(16, badgeSize - badgePad * 2 - 6);

  const shouldShowBadge = !!chain || !!customEmblemSrc || !!customEmblemIcon;

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  // Get position classes based on emblemPosition prop
  const getPositionClasses = () => {
    switch (emblemPosition) {
      case 'top-right':
        return '-top-1 -right-1';
      case 'top-left':
        return 'top-0 left-0';
      case 'bottom-left':
        return '-bottom-1 -left-1';
      case 'bottom-right':
      default:
        return '-bottom-1 -right-1';
    }
  };

  const positionClasses = getPositionClasses();

  return (
    <div
      className="relative inline-block"
      style={{ width: size, height: size }}
    >
      {/* Main icon */}
      <div
        className={cn('z-0 overflow-hidden', className)}
        style={{ width: size, height: size }}
      >
        {hasError ? (
          // Show placeholder token icon when no src or error
          <div className="flex h-full w-full items-center justify-center">
            <TokenFilledIcon
              className="h-full w-full"
              style={{ maxWidth: size * 0.7, maxHeight: size * 0.7 }}
            />
          </div>
        ) : (
          <>
            {/* Show skeleton while loading */}
            {isLoading && (
              <Skeleton className="absolute inset-0 z-10">
                <SkeletonMedia
                  className={className}
                  shape={
                    className?.includes('rounded-lg') ? 'rounded' : 'circle'
                  }
                  aspectRatio="1/1"
                  style={{ width: size, height: size }}
                />
              </Skeleton>
            )}

            {!isPlaceholderIcon ? (
              <img
                src={src!}
                alt={alt}
                className={cn(
                  'block h-full w-full object-contain select-none',
                  isLoading ? 'opacity-0' : 'opacity-100'
                )}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            ) : (
              <AppsFillIcon
                className="h-full w-full opacity-20"
                fill="tertiary"
              />
            )}
          </>
        )}
      </div>

      {/* Halo + Badge */}
      {shouldShowBadge && (
        <>
          <div
            aria-hidden
            className={cn(
              'absolute z-10 rounded-[6px]',
              'before:absolute before:inset-0 before:rounded-[6px] before:content-[""]',
              'before:-m-[var(--halo-size)] before:bg-[var(--halo-bg)]',
              positionClasses
            )}
            style={
              {
                width: badgeSize,
                height: badgeSize,
                '--halo-bg': haloBg,
                '--halo-size': `${haloSize}px`,
              } as React.CSSProperties
            }
          />
          <div
            className={cn('absolute z-20 rounded-[6px]', positionClasses)}
            style={{ width: badgeSize, height: badgeSize }}
          >
            <div className="grid h-full w-full place-items-center">
              {customEmblemIcon ? (
                <div
                  className="bg-surface flex items-center justify-center overflow-hidden rounded-[4px] [&>svg]:max-h-full [&>svg]:max-w-full"
                  style={{
                    width: glyphSize,
                    height: glyphSize,
                  }}
                >
                  {customEmblemIcon}
                </div>
              ) : customEmblemSrc ? (
                <img
                  src={customEmblemSrc}
                  alt="custom emblem"
                  className="bg-surface object-fit rounded-[4px]"
                  style={{ width: glyphSize, height: glyphSize }}
                />
              ) : (
                chain && (
                  <ChainEmblem
                    of={chain as any}
                    className="bg-surface rounded-[4px]"
                    style={{ width: glyphSize, height: glyphSize }}
                  />
                )
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default InfoImage;
